import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Stack,
  Breadcrumbs,
  Icon,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import _, { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthRepository } from "../repositories/AuthRepository";
import { TicketRepository } from "../repositories/TicketRepository";
import { HomeComponentBookingListBody } from "./HomeComponentBookingListBody";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@emotion/react";
import {
  clearReceiveNewTicket,
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../common/CommonActions";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import { FastBookTicket } from "./tickets/FastBookTicket";
import { HomeComponentTicketCommunication } from "./HomeComponentTicketCommunication";
import { HomeComponentBookingListBodyMobile } from "./mobile/HomeComponentBookingListBodyMobile";
import { OrganizationsRepository } from "../repositories/OrganizationsRepository";
import { jwtDecode } from "jwt-decode";
import { IntegrationInstructions } from "@mui/icons-material";

export default function ApiSettings() {
  const [loading, setLoading] = useState(false);
  const [organizationApiConfig, setOrganizationApiConfig] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = React.useState();
  const [active, setActive] = React.useState(false);
  const [loadingApiKey, setLoadingApiKey] = React.useState(false);
  const [regenerateApiKeyConfirmOpen, setRegenerateApiKeyConfirmOpen] =
    useState(false);
  const dispatch = useDispatch();

  React.useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    if (localStorage.getItem("jwt")) {
      setCurrentUser(jwtDecode(localStorage.getItem("jwt")));
    }
  };

  React.useEffect(() => {
    if (currentUser != null && currentUser?.organization?.id) {
      getOrganizationApiConfig(currentUser.organization.id);
    }
  }, [currentUser]);

  const getOrganizationApiConfig = (organizationId) => {
    setLoading(true);
    OrganizationsRepository.getApiConfig(organizationId)
      .then((response) => {
        setOrganizationApiConfig(response.data);
        setActive(response.data.active);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  };

  const reGenerateApiKey = () => {
    setLoadingApiKey(true);
    OrganizationsRepository.regenerateApiKey(
      organizationApiConfig?.organization?.id
    )
      .then((res) => {
        setLoadingApiKey(false);
        setRegenerateApiKeyConfirmOpen(false);
        setOrganizationApiConfig(res.data);
        dispatch(notifyShowSuccessMessage("API Key regenerated successfully!"));
      })
      .catch((err) => {
        console.log(err);
        setLoadingApiKey(false);
        setRegenerateApiKeyConfirmOpen(false);
        dispatch(notifyShowErrorMessage("Error regenerating API Key!"));
      });
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Stack
              direction="row"
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Typography
                variant="h5"
                key="2"
                color="text.primary"
                sx={{
                  fontWeight: "bold",
                  typography: { xs: "h4", md: "h5" },
                  alignContent: "center",
                  marginLeft: { xs: "8px", md: "0px" },
                }}
              >
                API Configuration Settings
                <IconButton
                  sx={{
                    border: "1px solid lightGray",
                    borderRadius: "5px",
                    padding: "1px",
                    minWidth: "auto",
                    height: "auto",
                    marginLeft: "5px",
                    display: { xs: "none", md: "inline-flex" },
                  }}
                  disabled
                >
                  <Icon
                    style={{ color: theme.palette.primary.main }}
                    fontSize="20"
                  >
                    integration_instructions
                  </Icon>
                </IconButton>
              </Typography>
            </Stack>
            <Divider style={{ width: "100%", marginTop: "10px" }} />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12}>
            {!loading && active ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 400, mb: 1, mt: 2 }}
                  >
                    API Key
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: 2,
                      backgroundColor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      px: 2,
                      py: 2,
                      mb: 2,
                      boxShadow: 1,
                      width: "100%",
                      overflowX: "auto",
                    }}
                  >
                    <Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                      >
                        <Icon
                          sx={{ mr: 1, color: "text.secondary" }}
                          fontSize="large"
                        >
                          vpn_key
                        </Icon>

                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 400,
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                            wordBreak: "break-all",
                            mr: 2,
                          }}
                        >
                          {organizationApiConfig?.apiKey ||
                            "No API Key available."}
                        </Typography>

                        {/* Status Indicator */}
                        {organizationApiConfig?.active !== undefined && (
                          <Chip
                            label={
                              organizationApiConfig.active
                                ? "Active"
                                : "Inactive"
                            }
                            color={
                              organizationApiConfig.active
                                ? "success"
                                : "default"
                            }
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              height: 24,
                              borderRadius: 1.5,
                            }}
                          />
                        )}
                      </Box>

                      {organizationApiConfig?.dateModified && (
                        <Typography
                          variant="body1"
                          sx={{ color: "text.secondary", ml: 5 }}
                        >
                          Last updated:{" "}
                          {new Date(
                            organizationApiConfig.dateModified
                          ).toLocaleString()}
                        </Typography>
                      )}
                    </Box>

                    {organizationApiConfig && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        onClick={() => setRegenerateApiKeyConfirmOpen(true)}
                        disabled={loadingApiKey}
                        startIcon={
                          loadingApiKey ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Icon>refresh</Icon>
                          )
                        }
                        sx={{
                          fontSize: "0.95rem",
                          height: "40px",
                          minWidth: { xs: "100%", sm: "140px" },
                          textTransform: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Regenerate
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  mb: 2,
                  boxShadow: 1,
                  textAlign: "center",
                }}
              >
                {loading ? (
                  <CircularProgress />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: "text.secondary", fontSize: "0.95rem" }}
                  >
                    The API is currently inactive. Activation is required to
                    proceed. <br />
                    Please reach out to our team for assistance.
                  </Typography>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
      <Dialog
        open={regenerateApiKeyConfirmOpen}
        onClose={() => setRegenerateApiKeyConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 2,
            py: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            pb: 1,
          }}
        >
          <Box display="flex" alignItems="center">
            <Icon sx={{ mr: 1, color: "warning.main" }} fontSize="large">
              warning
            </Icon>
            Regenerate API Key
          </Box>
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{ fontSize: "0.95rem", color: "text.secondary" }}
          >
            Are you sure you want to regenerate the API key? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, pt: 2 }}>
          <Button
            onClick={() => setRegenerateApiKeyConfirmOpen(false)}
            color="inherit"
            variant="outlined"
            disabled={loadingApiKey}
            sx={{
              textTransform: "none",
              fontSize: "0.95rem",
              height: "40px",
              minWidth: "100px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={reGenerateApiKey}
            color="primary"
            variant="contained"
            disabled={loadingApiKey}
            startIcon={
              loadingApiKey ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>refresh</Icon>
              )
            }
            sx={{
              textTransform: "none",
              fontSize: "0.95rem",
              height: "40px",
              minWidth: "120px",
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
